import { motion } from "framer-motion";
import { ArrowUpRight, Disc3, Radio, CircleDollarSign, Globe2 } from "lucide-react";
import { useRegisterModal } from "@/context/RegisterModalContext";
import { useLanguage } from "@/context/LanguageContext";
import { PORTAL_URL, CATALOG_STATUS_COLORS } from "@/lib/data";
import { RevealLine } from "./AnimatedSection";
import { StatCounter } from "./StatCounter";

const BADGE_MOTION = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const SUBTITLE_MOTION = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.5 } };
const CTA_ROW_MOTION = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.6 } };
const MOCKUP_MOTION = {
  initial: { opacity: 0, y: 30, rotateX: 6 },
  animate: { opacity: 1, y: 0, rotateX: 0 },
  transition: { duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] },
};

export const Hero = () => {
  const { openModal } = useRegisterModal();
  const { t } = useLanguage();
  const { hero } = t;

  return (
    <section id="inicio" data-testid="hero-section" className="relative pt-40 pb-28 md:pt-52 md:pb-40 overflow-x-hidden bg-[#0A0A0A]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-brand-blue/[0.16] via-transparent to-transparent animate-float" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-brand-red/[0.14] via-transparent to-transparent animate-float-slow" />
      <div
        className="absolute inset-0 opacity-[0.03] mix-blend-screen pointer-events-none"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.div
              {...BADGE_MOTION}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 mb-8"
            >
              <Globe2 className="w-3.5 h-3.5 text-brand-red" />
              <span className="text-[11px] font-semibold text-neutral-300 tracking-wide">{hero.badge}</span>
            </motion.div>

            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-8">
              <RevealLine text={hero.titleLines[0]} />
              <RevealLine text={hero.titleLines[1]} delay={0.08} />
              <RevealLine text={hero.titleLines[2]} delay={0.16} className="text-neutral-500" />
              <RevealLine text={hero.titleLines[3]} delay={0.24} />
            </h1>

            <motion.p
              {...SUBTITLE_MOTION}
              className="text-neutral-400 text-base md:text-lg leading-relaxed max-w-xl mb-10"
            >
              {hero.subtitle}
            </motion.p>

            <motion.div
              {...CTA_ROW_MOTION}
              className="flex flex-wrap items-center gap-4 mb-14"
            >
              <button
                data-testid="hero-register-button"
                onClick={openModal}
                className="group relative overflow-hidden bg-brand-red text-white text-sm font-bold px-7 py-3.5 rounded-full flex items-center gap-2 hover:-translate-y-0.5 hover:bg-brand-redDark animate-glow-pulse transition-all duration-300"
              >
                {hero.ctaRegister}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-login-button"
                className="text-sm font-semibold text-white px-7 py-3.5 rounded-full border border-brand-blue/40 hover:border-brand-blue hover:bg-brand-blue/10 transition-all duration-300"
              >
                {hero.ctaLogin}
              </a>
            </motion.div>

            <div data-testid="hero-stats-row" className="flex items-start gap-10 flex-wrap border-t border-white/10 pt-8">
              {hero.stats.map((stat, i) => (
                <StatCounter key={stat.id} id={stat.id} to={stat.to} prefix={stat.prefix} suffix={stat.suffix} label={stat.label} delay={i * 0.1} />
              ))}
            </div>
          </div>

          <motion.div
            {...MOCKUP_MOTION}
            style={{ perspective: 1200 }}
            className="lg:col-span-5"
          >
            <div
              data-testid="hero-catalog-mockup"
              className="rounded-3xl bg-black/40 backdrop-blur-2xl border border-white/10 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Disc3 className="w-4 h-4 text-white" />
                  <span className="text-white font-heading font-bold text-sm">{hero.catalogTitle}</span>
                </div>
                <span className="text-[11px] text-neutral-500 font-medium">{hero.catalogSubtitle}</span>
              </div>

              <div className="flex flex-col gap-3">
                {hero.items.map((item, i) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 + i * 0.1 }}
                    data-testid={`catalog-item-${i}`}
                    className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:border-white/20 transition-colors duration-300"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-white font-semibold text-sm">{item.name}</p>
                        <p className="text-neutral-500 text-xs">{item.type}</p>
                      </div>
                      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-neutral-300">
                        <span className={`w-1.5 h-1.5 rounded-full ${CATALOG_STATUS_COLORS[i]}`} />
                        {item.status}
                      </span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {hero.badgesByItem[i].map((b) => (
                        <span key={b} className="text-[10px] font-medium text-neutral-400 bg-white/[0.05] border border-white/10 rounded-full px-2.5 py-1">
                          {b}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="rounded-xl border border-brand-blue/20 bg-brand-blue/[0.06] p-3.5 flex items-center gap-2.5">
                  <CircleDollarSign className="w-4 h-4 text-brand-blue shrink-0" />
                  <div>
                    <p className="text-white font-bold text-sm leading-none">90%</p>
                    <p className="text-neutral-500 text-[10px]">{hero.statFundLabel}</p>
                  </div>
                </div>
                <div className="rounded-xl border border-brand-red/20 bg-brand-red/[0.06] p-3.5 flex items-center gap-2.5">
                  <Radio className="w-4 h-4 text-brand-red shrink-0" />
                  <div>
                    <p className="text-white font-bold text-sm leading-none">450+</p>
                    <p className="text-neutral-500 text-[10px]">{hero.statPlatformsLabel}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
